import React, { useState } from 'react'
import { TemplateType } from './Template'
import { IoIosArrowDropup } from 'react-icons/io'
import { IoIosArrowDropdown } from 'react-icons/io'

const HighlightsEntry = ({
  title,
  content,
}: {
  title: string
  content: string
}) => (
  <div className='self-stretch justify-start items-center gap-1.5 inline-flex'>
    <h4 className='text-[#083268] text-sm lg:text-lg font-medium lg:font-bold leading-normal tracking-wide'>
      {title + ':'}
    </h4>
    <span className='text-[#576885] text-sm lg:text-base font-normal leading-tight tracking-wide'>
      {content}
    </span>
  </div>
)

const HighlightEntryWithTags = ({
  title,
  tags,
  bgColor,
}: {
  title: string
  tags: Array<string>
  bgColor: string
}) => (
  <div className='self-stretch flex flex-col justify-start items-start gap-2'>
    <h4 className='text-[#083268] text-sm lg:text-lg font-medium lg:font-bold leading-normal tracking-wide'>
      {title + ':'}
    </h4>
    <div className='flex flex-wrap self-stretch justify-start items-center gap-2'>
      {tags.map((tag, index) => (
        <div
          key={tag + index}
          style={{ backgroundColor: bgColor }}
          className={`flex justify-start items-center gap-1 px-[6px] py-[2px] rounded-[10px]`}
        >
          <div className='text-center text-[#020250] text-xs font-medium'>
            {tag}
          </div>
        </div>
      ))}
    </div>
  </div>
)

const Divider = ({ className }: { className?: string }) => (
  <div
    className={`${className} self-stretch h-[0px] border border-[#9fb7d7]`}
  />
)

export const DocumentHighlights = ({
  document,
  metadata,
}: {
  document: TemplateType
  metadata
}) => {
  const [openHighlights, setOpenHighlights] = useState<boolean>(false)

  return (
    <div
      className={`flex flex-col bg-white rounded-2xl
                w-[320px] lg:w-[439px] p-6 lg:p-10
                ${
                  openHighlights
                    ? 'shadow-[-0.6000000238418579px_-0.6000000238418579px_12px_0px_rgba(39,92,141,0.12)]'
                    : 'shadow-[-10px_-10px_30px_0px_rgba(39,92,141,0.12)]'
                }
                lg:shadow-[-10px_-10px_30px_0px_rgba(39,92,141,0.12)]
            `}
    >
      <h3
        onClick={() => setOpenHighlights(!openHighlights)}
        className={`text-[#122d5c] 
                    w-full
                    cursor-pointer lg:cursor-default
                    text-sm lg:text-2xl 
                    font-medium lg:font-bold 
                    leading-relaxed tracking-wide
                    flex items-center justify-center gap-4 
                    ${openHighlights ? 'pb-5' : ''} lg:pb-[40px]`}
      >
        <span className='lg:w-full'>Document highlights</span>

        <IoIosArrowDropup
          className={`${
            openHighlights ? 'block' : 'hidden'
          } lg:hidden text-[#A0ABBE] w-5 h-5`}
          aria-label='Collapse highlights'
        />
        <IoIosArrowDropdown
          className={`${
            openHighlights ? 'hidden' : 'block'
          } lg:hidden text-[#A0ABBE] w-5 h-5`}
          aria-label='Expand highlights'
        />
      </h3>

      <div
        className={`${
          openHighlights ? 'flex' : 'hidden'
        } lg:flex flex-col gap-[20px] lg:gap-[40px]`}
      >
        <Divider className='lg:hidden' />

        <div className='flex flex-col justify-start items-start gap-2'>
          {metadata
            .filter((x) => x)
            .map(({ key, value }) => (
              <HighlightsEntry title={key} content={value} />
            ))}
        </div>

        {(!!document?.areas?.length ||
          !!document?.business_categories?.length ||
          !!document?.industries?.length) && (
          <>
            <Divider />
            <div className='flex-col justify-start items-start gap-6 flex'>
              {!!document?.areas?.length && (
                <HighlightEntryWithTags
                  title='Area of law'
                  tags={document.areas.map((category) => category)}
                  bgColor='#D3D3FF'
                />
              )}
              {!!document?.business_categories?.length && (
                <HighlightEntryWithTags
                  title='Business category'
                  tags={document?.business_categories?.map(
                    (business) => business.name,
                  )}
                  bgColor='#9FCDD7'
                />
              )}
              {!!document?.industries?.length && (
                <HighlightEntryWithTags
                  title='Industry'
                  tags={document?.industries?.map((industry) => industry.name)}
                  bgColor='#EBF3FF'
                />
              )}
            </div>
          </>
        )}

        <Divider />
        <div className='self-stretch flex-col justify-start items-start gap-3 flex'>
          <div className='self-stretch text-[#122d5c] text-sm lg:text-lg font-medium lg:font-bold leading-normal tracking-wide'>
            24/365 Access
          </div>
          <div className='self-stretch text-[#3f5985] text-sm lg:text-base leading-tight tracking-wide'>
            Automated drafter
          </div>
        </div>

        {document?.type?.name && (
          <>
            <Divider />
            <div className='text-center text-[#122d5c] text-sm lg:text-lg font-medium lg:font-bold leading-normal tracking-wide'>
              <h6>{document?.type?.name}</h6>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
